// Maps the Databricks-style headline-data response (manifest.schema.columns +
// result.data_array) into flat records, then into the report's delta/case-meta shapes.
// Mirrors the positional column mapping used in HeadlineData.tsx.

import { ReportCaseMeta, ReportDeltaMetric } from '../../../api/report-generator-API'
import { SvgXYPoint } from '../plot/SvgXYChart'

export type HeadlineRecord = Record<string, unknown>

const num = (v: unknown): number | undefined => {
  if (v === null || v === undefined || v === '') return undefined
  const n = typeof v === 'number' ? v : Number(v)
  return Number.isFinite(n) ? n : undefined
}

const str = (v: unknown): string | undefined =>
  v === null || v === undefined ? undefined : String(v)

export const mapHeadlineResult = (data: unknown): HeadlineRecord[] => {
  const d = data as {
    manifest?: { schema?: { columns?: { name: string; position: number }[] } }
    result?: { data_array?: unknown[][] }
  }
  const columns = d?.manifest?.schema?.columns
  const rows = d?.result?.data_array
  if (!Array.isArray(columns) || !Array.isArray(rows)) return []
  return rows.map(row => {
    const obj: HeadlineRecord = {}
    columns.forEach(col => {
      obj[col.name] = row[col.position]
    })
    return obj
  })
}

// runName filter is optional: when records are already scoped to a run's cases (the
// by-cases query), pass nothing and keep every record.
const recordsForRun = (records: HeadlineRecord[], runName?: string): HeadlineRecord[] =>
  runName ? records.filter(r => String(r.run ?? '') === runName) : records

/** Delta coefficient rows for a run's cases, shaped for ResultsTable. */
export const toDeltaRows = (records: HeadlineRecord[], runName?: string): ReportDeltaMetric[] =>
  recordsForRun(records, runName).map(r => ({
    caseName: String(r.state_name ?? r.gm_id ?? ''),
    gm_id: str(r.gm_id),
    ref_gm_id: str(r.ref_gm_id),
    delta_cd: num(r.delta_cd),
    delta_cl: num(r.delta_cl),
    delta_clf: num(r.delta_clf),
    delta_clr: num(r.delta_clr),
    delta_csf: num(r.delta_csf),
    delta_csr: num(r.delta_csr),
    delta_pct_clf: num(r.delta_pct_clf)
  }))

/** Attitude/identity metadata keyed by caseName, for ResultsTable's AHF/AHR/YAW columns. */
export const toCaseMeta = (
  records: HeadlineRecord[],
  runName?: string
): Record<string, ReportCaseMeta> => {
  const map: Record<string, ReportCaseMeta> = {}
  recordsForRun(records, runName).forEach(r => {
    const caseName = String(r.state_name ?? r.gm_id ?? '')
    map[caseName] = {
      caseName,
      gm_id: str(r.gm_id),
      runName,
      ahf: num(r.ahf),
      ahr: num(r.ahr),
      yaw: num(r.yaw),
      steer: num(r.steer),
      roll: num(r.roll),
      speed: num(r.speed)
    }
  })
  return map
}

// --- Charts --------------------------------------------------------------------

export const CHART_METRICS = [
  { key: 'delta_cd', label: 'Δ CD' },
  { key: 'delta_cl', label: 'Δ CL' },
  { key: 'delta_clf', label: 'Δ CLF' },
  { key: 'delta_clr', label: 'Δ CLR' }
] as const

export type ChartMetricKey = (typeof CHART_METRICS)[number]['key']

// Attitude axis for the x-value, chosen by what the records actually populate (series-dependent).
const ATTITUDE_FIELDS = ['yaw', 'speed', 'ahf', 'ahr', 'frh', 'rrh'] as const

export const pickAttitudeField = (records: HeadlineRecord[]): string | null => {
  for (const f of ATTITUDE_FIELDS) {
    if (records.some(r => num(r[f]) !== undefined)) return f
  }
  return null
}

/** Scatter points of a coefficient delta vs. an attitude (or case index when none). */
export const toChartPoints = (
  records: HeadlineRecord[],
  yKey: ChartMetricKey,
  xField: string | null
): SvgXYPoint[] =>
  records.reduce<SvgXYPoint[]>((acc, r, i) => {
    const y = num(r[yKey])
    if (y === undefined) return acc
    const x = xField ? num(r[xField]) : undefined
    acc.push({
      id: String(r.gm_id ?? i),
      name: String(r.state_name ?? r.gm_id ?? i),
      x: x ?? i,
      y
    })
    return acc
  }, [])
